import styled from 'styled-components';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { DynamicTable } from 'components/dynamicTable';
import {
  TableCell,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  Box,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { notification, Spin } from 'antd';
import { saveAs } from 'file-saver';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import {
  getReportDebitorBalanceSummary,
  getReportDebitorBalance,
  getReportDebitorPdf,
  getReportDebitorExcell,
  getInvoiceReportDownload,
} from 'services/reports';
import { getDebitorBalances } from 'services/tabreport';
import useTranslation from 'customHooks/translate';
import { truncateText } from 'utils';
import { PAGE_URL_KEY, REPORTS_PAGE_URL, tableHeaderStyle } from 'consts';
import TableDialog from 'pages/Dialogs/TableDialog';
import { ECountryCode } from 'enums';
import { getAccountData } from 'services/accounts';

import {
  ACCOUNT_INFO,
  PARTNER,
  REPORTS_DEBITORS,
  REPORTS_EXPENSES_SUMMARY,
} from '../../events/types';
import ReportPartnerSummary from './components/reportPartnerSummary';

const ArrayCell = ({
  data,
  renderItem,
  label,
  isDescription,
  firstElement,
}) => {
  const [open, setOpen] = useState(false);

  if (!data || data.length === 0) {
    return <div style={{ color: '#999' }}>{''}</div>;
  }

  if (data.length === 1 && !isDescription) {
    return <div>{renderItem(data[0], 0, data)}</div>;
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
          minWidth: 0,
        }}
      >
        {firstElement || renderItem(data[0], 0, data)}
      </div>
      <Chip
        label={data.length === 1 ? '...' : `+${data.length - 1}`}
        size="small"
        variant="outlined"
        color="primary"
        onClick={() => setOpen(true)}
        sx={{
          fontSize: '10px',
          height: '20px',
          cursor: 'pointer',
          flexShrink: 0,
          '&:hover': {
            backgroundColor: 'primary.light',
          },
        }}
      />

      <TableDialog handleClose={() => setOpen(false)} title={label} open={open}>
        {data?.map((item, idx) => (
          <Box
            className="p-[12px]"
            key={idx}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2">{renderItem(item, idx)}</Typography>
          </Box>
        ))}
      </TableDialog>
    </div>
  );
};

// Expandable Row Component
const ExpandableTableRow = ({
  item,
  index,
  translation,
  partnerId,
  isAccountCountryAZ,
}) => {
  const [open, setOpen] = useState(false);

  const setPageUrlForRedirect = () => {
    localStorage.setItem(PAGE_URL_KEY, REPORTS_PAGE_URL);
  };

  return (
    <>
      <TableRow>
        <TableCell
          align="center"
          sx={{
            fontSize: '13px',
            width: '50px',
            minWidth: '50px',
            maxWidth: '50px',
            padding: '8px 4px',
          }}
        >
          {index + 1}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: '13px',
            width: '100px',
            minWidth: '100px',
            padding: '8px 8px',
            whiteSpace: 'nowrap',
          }}
        >
          {item.invoiceJob}
        </TableCell>
        <TableCell
          align="center"
          sx={{
            fontSize: '13px',
            width: '120px',
            minWidth: '120px',
            padding: '8px 8px',
            whiteSpace: 'nowrap',
          }}
        >
          {item.managerName}
        </TableCell>
        {isAccountCountryAZ && (
          <>
            <TableCell
              align="center"
              sx={{
                fontSize: '13px',
                width: '120px',
                minWidth: '120px',
                padding: '8px 8px',
                whiteSpace: 'nowrap',
              }}
            >
              {item.eqaime ?? translation.subusers.noData}
            </TableCell>
            <TableCell
              align="center"
              sx={{
                fontSize: '13px',
                width: '120px',
                minWidth: '120px',
                padding: '8px 8px',
                whiteSpace: 'nowrap',
              }}
            >
              {item.eqaimeDate
                ? item.eqaimeDate.slice(0, 10)
                : translation.subusers.noData}
            </TableCell>
          </>
        )}
        <TableCell
          align="center"
          sx={{
            fontSize: '12px',
            width: '140px',
            minWidth: '140px',
            padding: '8px 8px',
          }}
        >
          <Link
            onClick={setPageUrlForRedirect}
            className="cusrsor-pointer whitespace-nowrap rounded-[5px] border border-[rgba(70,106,251,0.25)] p-[5px] text-[#466AFB]"
            to={`/invoices/${item.id}/detail/${partnerId}`}
          >
            {item.invoiceNumber}
          </Link>
        </TableCell>

        <TableCell
          align="center"
          sx={{
            fontSize: '13px',
            width: '120px',
            minWidth: '120px',
            padding: '8px 8px',
            whiteSpace: 'nowrap',
          }}
        >
          {item.invoiceDate.slice(0, 10)}
        </TableCell>

        {/* Description */}
        <TableCell
          align="center"
          sx={{
            fontSize: '13px',
            width: '180px',
            minWidth: '180px',
            maxWidth: '180px',
            padding: '8px 8px',
          }}
        >
          <ArrayCell
            data={item.invoiceLines}
            renderItem={(line) => line.description}
            label={translation.reports.Description}
            isDescription
            firstElement={truncateText(item?.invoiceLines[0]?.description, 10)}
          />
        </TableCell>

        {/* Total Amount */}
        <TableCell
          align="center"
          sx={{
            fontSize: '13px',
            width: '140px',
            minWidth: '140px',
            padding: '8px 8px',
            whiteSpace: 'nowrap',
          }}
        >
          <ArrayCell
            data={item.invoiceLines}
            renderItem={(line, idx, allData) => {
              if (allData && allData.length > 1) {
                const totalSum = allData.reduce((sum, lineItem) => {
                  const amount = parseFloat(lineItem.totalAmount) || 0;
                  return sum + amount;
                }, 0);
                return `${new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(totalSum)} ${item?.currency}`;
              }
              return `${new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(line?.totalAmount || 0)} ${item?.currency}`;
            }}
            label={translation.reports.Amount}
          />
        </TableCell>

        {/* Payment Date */}
        <TableCell
          align="center"
          sx={{
            fontSize: '13px',
            width: '120px',
            minWidth: '120px',
            padding: '8px 8px',
            whiteSpace: 'nowrap',
          }}
        >
          <ArrayCell
            data={item.payments}
            renderItem={(payment) =>
              payment.paymentDate
                ? payment.paymentDate.slice(0, 10)
                : translation.subusers.noData
            }
            label="Ödəniş tarixləri"
          />
        </TableCell>

        {/* Paid Amount */}
        <TableCell
          align="center"
          sx={{
            fontSize: '13px',
            width: '140px',
            minWidth: '140px',
            padding: '8px 8px',
            whiteSpace: 'nowrap',
          }}
        >
          <ArrayCell
            data={item.payments}
            renderItem={(payment) =>
              !!payment?.paymentAmount
                ? `${new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(payment?.paymentAmount)} ${item?.currency}`
                : '-'
            }
            label="Ödənilmiş məbləğlər"
          />
        </TableCell>

        {/* Remain Balance */}
        <TableCell
          align="center"
          sx={{
            fontSize: '13px',
            width: '140px',
            minWidth: '140px',
            padding: '8px 8px',
            whiteSpace: 'nowrap',
          }}
        >
          <ArrayCell
            data={item.payments}
            renderItem={(payment) =>
              !!payment?.remainBalance
                ? `${new Intl.NumberFormat('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(payment?.remainBalance)} ${item?.currency}`
                : '-'
            }
          />
        </TableCell>

        {/* Payment Method */}
        <TableCell
          align="center"
          sx={{
            fontSize: '13px',
            width: '130px',
            minWidth: '130px',
            padding: '8px 8px',
            whiteSpace: 'nowrap',
          }}
        >
          <ArrayCell
            data={item.payments}
            renderItem={(payment) =>
              payment.paymentMethodType
                ? payment.paymentMethodType
                : translation.subusers.noData
            }
            label="Ödəniş üsulları"
          />
        </TableCell>

        {/* Expand Button */}
        <TableCell
          align="center"
          sx={{
            fontSize: '13px',
            width: '60px',
            minWidth: '60px',
            maxWidth: '60px',
            padding: '8px 4px',
          }}
        >
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              color: '#466AFB',
              '&:hover': {
                backgroundColor: 'rgba(70, 106, 251, 0.1)',
              },
            }}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Expandable Content Row */}
      <TableRow>
        <TableCell
          align="center"
          colSpan={isAccountCountryAZ ? 14 : 12}
          sx={{
            paddingBottom: 0,
            paddingTop: 0,
            border: 'none',
          }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 1,
                padding: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 1,
                border: '1px solid #e9ecef',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '16px',
                }}
              >
                {/* Invoice Lines */}
                {item.invoiceLines && item.invoiceLines.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        color: '#466AFB',
                        fontWeight: 500,
                      }}
                    >
                      Faktura sətrləri:
                    </Typography>
                    {item.invoiceLines.map((line, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          backgroundColor: 'white',
                          borderRadius: 1,
                          border: '1px solid #dee2e6',
                          fontSize: '13px',
                        }}
                      >
                        <div>
                          <strong>{translation.reports.Description}:</strong>{' '}
                          {line.description}
                        </div>
                        <div>
                          <strong>{translation.reports.Amount}:</strong>{' '}
                          {line.totalAmount}
                        </div>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Payments */}
                {item.payments && item.payments.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1,
                        color: '#466AFB',
                        fontWeight: 500,
                      }}
                    >
                      Ödənişlər:
                    </Typography>
                    {item.payments.map((payment, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          p: 1.5,
                          mb: 1,
                          backgroundColor: 'white',
                          borderRadius: 1,
                          border: '1px solid #dee2e6',
                          fontSize: '13px',
                        }}
                      >
                        <div>
                          <strong>Tarix:</strong>{' '}
                          {payment.paymentDate
                            ? payment.paymentDate.slice(0, 10)
                            : translation.subusers.noData}
                        </div>
                        <div>
                          <strong>{translation.reports.Amount}:</strong>{' '}
                          {payment.paymentAmount
                            ? `${new Intl.NumberFormat('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }).format(payment.paymentAmount)} ${
                                item?.currency
                              }`
                            : translation.subusers.noData}
                        </div>
                        <div>
                          <strong>Üsul:</strong>{' '}
                          {payment.paymentMethodType
                            ? payment.paymentMethodType
                            : translation.subusers.noData}
                        </div>
                      </Box>
                    ))}
                  </Box>
                )}
              </div>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const ReportsDebitors = () => {
  const translation = useTranslation();
  const navigate = useNavigate();
  const { id: idFromRoute } = useParams();

  const [shouldFetch, setShouldFetch] = useState(true);
  const [partnerId, setPartnerId] = useState(null);

  const accountInfoQuery = useQuery({
    queryKey: [ACCOUNT_INFO],
    queryFn: getAccountData,
    select: (response) => response?.data?.data,
  });

  const countryCode = localStorage.getItem('customerCountryCode');
  const isAccountCountryAZ =
    accountInfoQuery.data?.countryCode === ECountryCode.AZ ||
    countryCode === ECountryCode.AZ;

  const [pageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [invoicesData, setInvoicesData] = useState([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState('');

  const tableContainerRef = useRef(null);
  const tableHorizontalScrollRef = useRef(null);
  const topScrollbarRef = useRef(null);
  const topScrollbarContentRef = useRef(null);
  const loadingRef = useRef(null);
  const isFetchingRef = useRef(false);
  const mountCountRef = useRef(0);
  const isSyncingHorizontalScrollRef = useRef(false);

  const scrollPositionRef = useRef(0);
  const dataStateRef = useRef({
    invoicesData: [],
    pageIndex: 0,
    hasMoreData: true,
    totalRecords: 0,
  });

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (tableContainerRef.current) {
        scrollPositionRef.current = tableContainerRef.current.scrollTop;
      }
      dataStateRef.current = {
        invoicesData,
        pageIndex,
        hasMoreData,
        totalRecords: invoicesData.length,
      };
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleBeforeUnload();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [invoicesData, pageIndex, hasMoreData]);

  useEffect(() => {
    mountCountRef.current += 1;

    if (idFromRoute) {
      setPartnerId(idFromRoute);

      // BUG FIX: Check if we have preserved data for this partner
      const shouldRestoreData =
        dataStateRef.current.invoicesData.length > 0 &&
        dataStateRef.current.totalRecords > 10;

      if (shouldRestoreData) {
        setInvoicesData(dataStateRef.current.invoicesData);
        setPageIndex(dataStateRef.current.pageIndex);
        setHasMoreData(dataStateRef.current.hasMoreData);
        setShouldFetch(false);

        setTimeout(() => {
          if (tableContainerRef.current && scrollPositionRef.current > 0) {
            tableContainerRef.current.scrollTop = scrollPositionRef.current;
          }
        }, 100);
      } else {
        resetData();
        setShouldFetch(true);
      }
    }

    return () => {};
  }, [idFromRoute]);

  const resetData = () => {
    setInvoicesData([]);
    setPageIndex(0);
    setHasMoreData(true);
    scrollPositionRef.current = 0;
    dataStateRef.current = {
      invoicesData: [],
      pageIndex: 0,
      hasMoreData: true,
      totalRecords: 0,
    };
  };

  const openNotification = (message, type = 'warning') => {
    notification[type]({
      message,
      duration: 2,
    });
  };

  const { data: partnersData, isSuccess: isPartnersSuccess } = useQuery({
    queryKey: [PARTNER],
    queryFn: () => getDebitorBalances(100, 0),
    select: (response) => {
      if (!response?.data?.data || !Array.isArray(response.data.data)) {
        return [];
      }
      return response.data.data.map((d) => ({ label: d.name, value: d.id }));
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    onSuccess: () => {
      if (partnerId) {
        setShouldFetch(true);
      }
    },
  });

  const fetchInvoiceData = useCallback(
    async (currentPage, isNewRequest = false) => {
      if (isFetchingRef.current || !partnerId) return null;

      isFetchingRef.current = true;
      setIsLoadingMore(true);

      try {
        const response = await getReportDebitorBalance(
          partnerId,
          pageSize,
          currentPage
        );
        const data = response.data;
        setSelectedPartner(data.partnerName);

        if (data?.invoicesWithDetails?.length) {
          const newInvoicesData = isNewRequest
            ? data.invoicesWithDetails
            : [...invoicesData, ...data.invoicesWithDetails];

          setInvoicesData(newInvoicesData);
          dataStateRef.current.invoicesData = newInvoicesData;
          dataStateRef.current.pageIndex = currentPage;
          dataStateRef.current.totalRecords = data.totalRecord;

          const newTotalItems = newInvoicesData.length;
          const hasMore = newTotalItems < data.totalRecord;
          setHasMoreData(hasMore);
          dataStateRef.current.hasMoreData = hasMore;

          return data;
        }

        setHasMoreData(false);
        dataStateRef.current.hasMoreData = false;
        return null;
      } catch {
        return null;
      } finally {
        setIsLoadingMore(false);
        isFetchingRef.current = false;
      }
    },
    [partnerId, pageSize, invoicesData]
  );

  const { refetch: refetchInvoices } = useQuery({
    queryKey: [REPORTS_DEBITORS, partnerId, mountCountRef.current],
    queryFn: () => fetchInvoiceData(0, true),
    enabled: !!partnerId && shouldFetch,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    onSettled: () => {
      setShouldFetch(false);
    },
  });

  useEffect(() => {
    if (partnerId) {
      resetData();
      setShouldFetch(true);
      refetchInvoices();
    }
  }, [partnerId, refetchInvoices]);

  const { data: summaryData, refetch: refetchSummary } = useQuery({
    queryKey: [REPORTS_EXPENSES_SUMMARY, partnerId, mountCountRef.current],
    queryFn: () => getReportDebitorBalanceSummary(idFromRoute),
    select: (res) => res.data,
    enabled: !!partnerId,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const handleTableScroll = useCallback(() => {
    if (
      !tableContainerRef.current ||
      !loadingRef.current ||
      isFetchingRef.current ||
      !hasMoreData
    )
      return;
    scrollPositionRef.current = tableContainerRef.current.scrollTop;

    const containerRect = tableContainerRef.current.getBoundingClientRect();
    const loadingRect = loadingRef.current.getBoundingClientRect();

    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = pageIndex + 1;
      setPageIndex(nextPage);
      fetchInvoiceData(nextPage);
    }
  }, [pageIndex, hasMoreData, fetchInvoiceData]);

  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener('scroll', handleTableScroll);
      return () => {
        tableContainer.removeEventListener('scroll', handleTableScroll);
      };
    }
  }, [handleTableScroll]);

  useEffect(() => {
    const tableContainer = tableHorizontalScrollRef.current;
    const topScrollbar = topScrollbarRef.current;
    const topScrollbarContent = topScrollbarContentRef.current;

    if (!tableContainer || !topScrollbar || !topScrollbarContent) return;

    const syncTopScrollbarWidth = () => {
      topScrollbarContent.style.width = `${tableContainer.scrollWidth}px`;
    };

    const handleBottomHorizontalScroll = () => {
      if (isSyncingHorizontalScrollRef.current) return;
      isSyncingHorizontalScrollRef.current = true;
      topScrollbar.scrollLeft = tableContainer.scrollLeft;
      isSyncingHorizontalScrollRef.current = false;
    };

    const handleTopHorizontalScroll = () => {
      if (isSyncingHorizontalScrollRef.current) return;
      isSyncingHorizontalScrollRef.current = true;
      tableContainer.scrollLeft = topScrollbar.scrollLeft;
      isSyncingHorizontalScrollRef.current = false;
    };

    syncTopScrollbarWidth();
    topScrollbar.scrollLeft = tableContainer.scrollLeft;

    tableContainer.addEventListener('scroll', handleBottomHorizontalScroll);
    topScrollbar.addEventListener('scroll', handleTopHorizontalScroll);
    window.addEventListener('resize', syncTopScrollbarWidth);

    return () => {
      tableContainer.removeEventListener(
        'scroll',
        handleBottomHorizontalScroll
      );
      topScrollbar.removeEventListener('scroll', handleTopHorizontalScroll);
      window.removeEventListener('resize', syncTopScrollbarWidth);
    };
  }, [invoicesData, isAccountCountryAZ]);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleExcelExport = async () => {
    try {
      openNotification(
        'Excel report is being prepared, please wait...',
        'info'
      );

      const excelResponse = await getReportDebitorExcell(partnerId);
      const reportId = excelResponse?.data?.[0]?.guidId;

      if (reportId) {
        await delay(4000);
        const downloadResponse = await getInvoiceReportDownload(reportId);

        const blob = new Blob([downloadResponse.data], {
          type: 'application/vnd.ms-excel',
        });
        saveAs(blob, `report_${reportId}.xlsx`);

        openNotification(
          'Excel report generated and download started',
          'success'
        );
      } else {
        openNotification(`${translation.reports.Failed}`, 'error');
      }
    } catch (error) {
      openNotification(`${translation.reports.Failed}`, 'error');
    }
  };

  const handlePdfExport = async () => {
    try {
      openNotification(`${translation.reports.PdfWait}`, 'info');

      const excelResponse = await getReportDebitorPdf(partnerId);
      const reportId = excelResponse?.data?.[0]?.guidId;

      if (reportId) {
        await delay(4000);
        const downloadResponse = await getInvoiceReportDownload(reportId);

        const blob = new Blob([downloadResponse.data], {
          type: 'application/pdf',
        });
        saveAs(blob, `report_${reportId}.pdf`);

        openNotification(`${translation.reports.PdfGenerated}`, 'success');
      } else {
        openNotification(`${translation.reports.Failed}`, 'error');
      }
    } catch (error) {
      openNotification(`${translation.reports.Failed}`, 'error');
    }
  };

  const handleGoBack = () => {
    if (selectedPartner) {
      localStorage.setItem('highlightPartner', selectedPartner);
      localStorage.setItem('scrollToPartner', 'true');
      localStorage.setItem('highlightTimestamp', Date.now().toString());
    }

    navigate(-1);
  };

  useEffect(() => {
    if (idFromRoute) {
      setPartnerId(idFromRoute);

      if (
        isPartnersSuccess &&
        partnersData?.length > 0 &&
        dataStateRef.current.invoicesData.length === 0
      ) {
        setShouldFetch(true);
        refetchInvoices();
        refetchSummary();
      }
    }
  }, [
    idFromRoute,
    isPartnersSuccess,
    partnersData,
    refetchInvoices,
    refetchSummary,
  ]);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-[24px] px-[16px] py-[12px] md:px-[32px]">
        <div className="flex w-full items-center justify-between gap-[24px] md:w-auto md:justify-start">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-[5px] rounded-[8px] bg-[#F6F6F6] px-[20px] py-[8px] text-[14px] font-medium text-black"
          >
            <ArrowBackIosNewIcon style={{ width: '14px', height: '14px' }} />
            {translation.subusers.back}
          </button>
          <p className="text-[18px] font-semibold text-[#1D1D1D] md:text-[20px]">
            {selectedPartner}
          </p>
        </div>
        {invoicesData?.length > 0 && (
          <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[12px] md:w-[unset] md:grid-cols-2">
            <button
              onClick={handleExcelExport}
              className="flex w-full items-center gap-[8px] rounded-[6px] rounded-[6px] border-2  border-[#CECECE] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:px-[24px] md:py-[12px]"
            >
              <img
                className="mr-[5px] h-5 w-5"
                src="./assets/save-folder-icon.png"
                alt=""
              />
              <p>{translation.reports.SaveAsExcel}</p>
            </button>
            <button
              onClick={handlePdfExport}
              className="flex w-full items-center gap-[8px] rounded-[6px] rounded-[6px] border-2 border-[#CECECE] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-black transition-all duration-300 ease-in-out hover:bg-gray-100 md:px-[24px] md:py-[12px]"
            >
              <img
                className="mr-[5px] h-5 w-5"
                src="./assets/save-folder-icon.png"
                alt=""
              />
              <p>{translation.reports.SaveAsPdf}</p>
            </button>
          </div>
        )}
      </div>
      {invoicesData?.length > 0 && (
        <ReportPartnerSummary
          title={translation.reports.AllAmount}
          summaryData={summaryData}
        />
      )}
      <div
        ref={topScrollbarRef}
        className="h-[16px] overflow-x-auto overflow-y-hidden"
      >
        <div ref={topScrollbarContentRef} className="h-[2px]" />
      </div>
      <div
        className="relative max-h-[600px] overflow-x-auto overflow-y-auto"
        ref={tableContainerRef}
      >
        <DynamicTable
          bordered
          hasData={invoicesData?.length > 0}
          cells={[
            '',
            `${translation.reports.JobNumber}`,
            `${translation.reports.Manager}`,
            ...(isAccountCountryAZ
              ? [
                  `${translation.electronicInvoice.LabelText}`,
                  `${translation.electronicInvoice.LabelDateText}`,
                ]
              : []),
            `${translation.reports.InvoiceNo}`,
            `${translation.InvoiceDetails.InvoiceDate}`,
            `${translation.reports.Description}`,
            `${translation.reports.TotalAmount}`,
            `${translation.reports.PaymentDate}`,
            `${translation.reports.PaidAmount}`,
            `${translation.reports.RemindAmount}`,
            `${translation.reports.PaymentMethod}`,
          ]}
          headerCellStyle={tableHeaderStyle}
          tableContainerRef={tableHorizontalScrollRef}
        >
          {invoicesData.map((item, index) => (
            <ExpandableTableRow
              key={index}
              item={item}
              index={index}
              translation={translation}
              partnerId={partnerId}
              isAccountCountryAZ={isAccountCountryAZ}
            />
          ))}
        </DynamicTable>

        <LoadingIndicator ref={loadingRef}>
          {isLoadingMore && <Spin size="small" />}
          {!hasMoreData && invoicesData.length > 0 && (
            <div style={{ color: '#999', marginTop: '8px' }}>
              {translation.messages?.allData}
            </div>
          )}
        </LoadingIndicator>
      </div>
    </div>
  );
};

export default ReportsDebitors;

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50px;
`;
